import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';

const propertyTypes = [
  { id: 'strip', name: 'ストリップ', baseROI: 0.26, minInvestment: 500000, depreciationYears: 5 },
  { id: 'highway', name: 'ハイウェイ', baseROI: 0.22, minInvestment: 300000, depreciationYears: 7 },
  { id: 'downtown', name: 'ダウンタウン', baseROI: 0.20, minInvestment: 250000, depreciationYears: 7 },
  { id: 'airport', name: '空港周辺', baseROI: 0.24, minInvestment: 400000, depreciationYears: 5 },
];

const contractTerms = [
  { years: 3, bonus: 0 },
  { years: 5, bonus: 0.02 },
  { years: 7, bonus: 0.04 },
  { years: 10, bonus: 0.06 },
];

const taxRates = [
  { id: 'japan_corp', name: '日本法人（実効税率）', rate: 0.30 },
  { id: 'japan_high', name: '日本個人（高所得）', rate: 0.45 },
  { id: 'japan_mid', name: '日本個人（中所得）', rate: 0.33 },
  { id: 'us_corp', name: '米国法人', rate: 0.21 },
  { id: 'us_individual', name: '米国個人（高所得）', rate: 0.37 },
];

type SimulatorTab = 'revenue' | 'tax';

export default function SimulatorTool() {
  const [activeTab, setActiveTab] = useState<SimulatorTab>('revenue');
  const [investmentAmount, setInvestmentAmount] = useState(500000);
  const [selectedProperty, setSelectedProperty] = useState(propertyTypes[0]);
  const [selectedTerm, setSelectedTerm] = useState(contractTerms[1]);
  const [showResults, setShowResults] = useState(false);

  // Tax calculator states
  const [taxInvestment, setTaxInvestment] = useState(500000);
  const [selectedTaxProperty, setSelectedTaxProperty] = useState(propertyTypes[0]);
  const [selectedTaxRate, setSelectedTaxRate] = useState(taxRates[0]);
  const [annualIncome, setAnnualIncome] = useState(1000000);
  const [showTaxResults, setShowTaxResults] = useState(false);

  const results = useMemo(() => {
    const annualROI = selectedProperty.baseROI + selectedTerm.bonus;
    const annualIncome = investmentAmount * annualROI;
    const totalIncome = annualIncome * selectedTerm.years;
    const totalReturn = investmentAmount + totalIncome;
    const monthlyIncome = annualIncome / 12;
    const paybackPeriod = investmentAmount / annualIncome;

    return {
      annualROI: (annualROI * 100).toFixed(1),
      annualIncome: Math.round(annualIncome),
      totalIncome: Math.round(totalIncome),
      totalReturn: Math.round(totalReturn),
      monthlyIncome: Math.round(monthlyIncome),
      paybackPeriod: paybackPeriod.toFixed(1),
    };
  }, [investmentAmount, selectedProperty, selectedTerm]);

  const taxResults = useMemo(() => {
    const depreciationYears = selectedTaxProperty.depreciationYears;
    const annualDepreciation = taxInvestment / depreciationYears;
    const taxSavingsPerYear = annualDepreciation * selectedTaxRate.rate;
    const totalTaxSavings = taxSavingsPerYear * depreciationYears;
    const effectiveCost = taxInvestment - totalTaxSavings;
    const taxSavingsRate = (totalTaxSavings / taxInvestment) * 100;
    
    // Calculate tax reduction on annual income
    const taxBeforeInvestment = annualIncome * selectedTaxRate.rate;
    const taxableIncomeAfter = Math.max(0, annualIncome - annualDepreciation);
    const taxAfterInvestment = taxableIncomeAfter * selectedTaxRate.rate;
    const annualTaxReduction = taxBeforeInvestment - taxAfterInvestment;

    return {
      depreciationYears,
      annualDepreciation: Math.round(annualDepreciation),
      taxSavingsPerYear: Math.round(taxSavingsPerYear),
      totalTaxSavings: Math.round(totalTaxSavings),
      effectiveCost: Math.round(effectiveCost),
      taxSavingsRate: taxSavingsRate.toFixed(1),
      taxBeforeInvestment: Math.round(taxBeforeInvestment),
      taxAfterInvestment: Math.round(taxAfterInvestment),
      annualTaxReduction: Math.round(annualTaxReduction),
    };
  }, [taxInvestment, selectedTaxProperty, selectedTaxRate, annualIncome]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleSimulate = () => {
    setShowResults(true);
  };

  const handleTaxSimulate = () => {
    setShowTaxResults(true);
  };

  return (
    <section id="simulator" className="py-24 px-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-1.5 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-4">
            INVESTMENT SIMULATOR
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            ビルボード投資シミュレーション
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            ラスベガス屋外広告ビルボードの収益予測と税務メリットを即座に確認できます
          </p>
        </motion.div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-gray-200 rounded-full p-1">
            <button
              onClick={() => setActiveTab('revenue')}
              className={`px-6 py-3 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'revenue'
                  ? 'bg-black text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-line-chart-line mr-2"></i>
              収益シミュレーション
            </button>
            <button
              onClick={() => setActiveTab('tax')}
              className={`px-6 py-3 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'tax'
                  ? 'bg-black text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-calculator-line mr-2"></i>
              税務メリット計算
            </button>
          </div>
        </div>

        {/* Revenue Simulator */}
        {activeTab === 'revenue' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input Panel */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <i className="ri-settings-4-line text-amber-600 text-xl"></i>
                </div>
                ビルボード投資条件を設定
              </h3>

              {/* Investment Amount */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  投資額
                </label>
                <div className="relative">
                  <input
                    type="range"
                    min="100000"
                    max="2000000"
                    step="50000"
                    value={investmentAmount}
                    onChange={(e) => setInvestmentAmount(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-amber-500"
                  />
                  <div className="flex justify-between mt-2 text-xs text-gray-500">
                    <span>$100,000</span>
                    <span>$2,000,000</span>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <span className="text-4xl font-bold text-gray-900">
                    {formatCurrency(investmentAmount)}
                  </span>
                </div>
              </div>

              {/* Property Type */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  ビルボード物件タイプ
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {propertyTypes.map((property) => (
                    <button
                      key={property.id}
                      onClick={() => setSelectedProperty(property)}
                      className={`p-4 rounded-xl border-2 transition-all cursor-pointer text-left ${
                        selectedProperty.id === property.id
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold text-gray-900">{property.name}</div>
                      <div className="text-sm text-gray-500 mt-1">
                        基本ROI {(property.baseROI * 100).toFixed(0)}%
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Contract Term */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  広告契約期間
                </label>
                <div className="flex gap-3">
                  {contractTerms.map((term) => (
                    <button
                      key={term.years}
                      onClick={() => setSelectedTerm(term)}
                      className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all cursor-pointer ${
                        selectedTerm.years === term.years
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold text-gray-900">{term.years}年</div>
                      {term.bonus > 0 && (
                        <div className="text-xs text-amber-600 mt-1">
                          +{(term.bonus * 100).toFixed(0)}% ボーナス
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={handleSimulate}
                className="w-full py-4 bg-black text-white font-semibold rounded-xl hover:bg-gray-800 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-calculator-line text-xl"></i>
                広告収益を計算する
              </button>
            </motion.div>

            {/* Results Panel */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className={`rounded-3xl p-8 transition-all ${
                showResults
                  ? 'bg-gradient-to-br from-gray-900 to-gray-800'
                  : 'bg-gray-100'
              }`}
            >
              {showResults ? (
                <>
                  <h3 className="text-xl font-bold text-white mb-8 flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
                      <i className="ri-line-chart-line text-black text-xl"></i>
                    </div>
                    ROI・収益シミュレーション結果
                  </h3>

                  <div className="space-y-6">
                    {/* Annual ROI */}
                    <div className="bg-white/10 rounded-2xl p-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400 text-sm">年間ROI</span>
                        <span className="text-amber-400 text-sm font-medium">
                          {selectedProperty.name} × {selectedTerm.years}年契約
                        </span>
                      </div>
                      <div className="text-5xl font-bold text-white">
                        {results.annualROI}%
                      </div>
                    </div>

                    {/* Income Grid */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/10 rounded-2xl p-5">
                        <div className="text-gray-400 text-sm mb-1">月間収入</div>
                        <div className="text-2xl font-bold text-white">
                          {formatCurrency(results.monthlyIncome)}
                        </div>
                      </div>
                      <div className="bg-white/10 rounded-2xl p-5">
                        <div className="text-gray-400 text-sm mb-1">年間収入</div>
                        <div className="text-2xl font-bold text-white">
                          {formatCurrency(results.annualIncome)}
                        </div>
                      </div>
                    </div>

                    {/* Total Returns */}
                    <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-amber-400 text-sm">
                          {selectedTerm.years}年間の総収益
                        </span>
                        <i className="ri-trophy-line text-amber-400 text-xl"></i>
                      </div>
                      <div className="text-4xl font-bold text-white mb-2">
                        {formatCurrency(results.totalIncome)}
                      </div>
                      <div className="text-gray-400 text-sm">
                        投資回収期間: 約{results.paybackPeriod}年
                      </div>
                    </div>

                    {/* Total Value */}
                    <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                      <div className="text-gray-400 text-sm mb-2">
                        契約終了時の総資産価値
                      </div>
                      <div className="text-3xl font-bold text-white">
                        {formatCurrency(results.totalReturn)}
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-green-400 text-sm">
                        <i className="ri-arrow-up-line"></i>
                        <span>
                          +{((results.totalReturn / investmentAmount - 1) * 100).toFixed(0)}% 増加
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-white/10">
                    <p className="text-gray-500 text-xs mb-4">
                      ※ 本シミュレーションは過去の実績に基づく予測であり、将来の収益を保証するものではありません。
                    </p>
                    <a
                      href="/#contact"
                      className="w-full py-4 bg-amber-500 text-black font-semibold rounded-xl hover:bg-amber-400 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                      詳細な投資相談を予約
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center py-16">
                  <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mb-6">
                    <i className="ri-calculator-line text-gray-400 text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    条件を設定してください
                  </h3>
                  <p className="text-gray-500 text-sm max-w-xs">
                    左側のパネルで投資条件を設定し、「広告収益を計算する」ボタンをクリックしてください
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        )}

        {/* Tax Benefits Simulator */}
        {activeTab === 'tax' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Tax Input Panel */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <i className="ri-money-dollar-circle-line text-emerald-600 text-xl"></i>
                </div>
                ビルボード投資税務条件を設定
              </h3>

              {/* Tax Investment Amount */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  ビルボード投資額（減価償却対象）
                </label>
                <div className="relative">
                  <input
                    type="range"
                    min="100000"
                    max="2000000"
                    step="50000"
                    value={taxInvestment}
                    onChange={(e) => setTaxInvestment(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-emerald-500"
                  />
                  <div className="flex justify-between mt-2 text-xs text-gray-500">
                    <span>$100,000</span>
                    <span>$2,000,000</span>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <span className="text-4xl font-bold text-gray-900">
                    {formatCurrency(taxInvestment)}
                  </span>
                </div>
              </div>

              {/* Annual Income */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  年間課税所得
                </label>
                <div className="relative">
                  <input
                    type="range"
                    min="100000"
                    max="5000000"
                    step="100000"
                    value={annualIncome}
                    onChange={(e) => setAnnualIncome(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-emerald-500"
                  />
                  <div className="flex justify-between mt-2 text-xs text-gray-500">
                    <span>$100,000</span>
                    <span>$5,000,000</span>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <span className="text-3xl font-bold text-gray-900">
                    {formatCurrency(annualIncome)}
                  </span>
                </div>
              </div>

              {/* Property Type for Depreciation */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  ビルボード物件タイプ（償却期間）
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {propertyTypes.map((property) => (
                    <button
                      key={property.id}
                      onClick={() => setSelectedTaxProperty(property)}
                      className={`p-4 rounded-xl border-2 transition-all cursor-pointer text-left ${
                        selectedTaxProperty.id === property.id
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold text-gray-900">{property.name}</div>
                      <div className="text-sm text-gray-500 mt-1">
                        償却期間 {property.depreciationYears}年
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tax Rate */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  適用税率
                </label>
                <div className="space-y-2">
                  {taxRates.map((tax) => (
                    <button
                      key={tax.id}
                      onClick={() => setSelectedTaxRate(tax)}
                      className={`w-full p-4 rounded-xl border-2 transition-all cursor-pointer text-left flex items-center justify-between ${
                        selectedTaxRate.id === tax.id
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="font-medium text-gray-900">{tax.name}</span>
                      <span className={`text-sm font-semibold ${
                        selectedTaxRate.id === tax.id ? 'text-emerald-600' : 'text-gray-500'
                      }`}>
                        {(tax.rate * 100).toFixed(0)}%
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={handleTaxSimulate}
                className="w-full py-4 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-calculator-line text-xl"></i>
                税務メリットを計算
              </button>
            </motion.div>

            {/* Tax Results Panel */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className={`rounded-3xl p-8 transition-all ${
                showTaxResults
                  ? 'bg-gradient-to-br from-emerald-900 to-emerald-800'
                  : 'bg-gray-100'
              }`}
            >
              {showTaxResults ? (
                <>
                  <h3 className="text-xl font-bold text-white mb-8 flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-400 rounded-xl flex items-center justify-center">
                      <i className="ri-percent-line text-emerald-900 text-xl"></i>
                    </div>
                    ビルボード投資税務メリット計算結果
                  </h3>

                  <div className="space-y-6">
                    {/* Depreciation Overview */}
                    <div className="bg-white/10 rounded-2xl p-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-emerald-300 text-sm">減価償却スケジュール</span>
                        <span className="text-emerald-400 text-sm font-medium">
                          {selectedTaxProperty.name} / {taxResults.depreciationYears}年償却
                        </span>
                      </div>
                      <div className="text-4xl font-bold text-white mb-1">
                        {formatCurrency(taxResults.annualDepreciation)}
                        <span className="text-lg text-emerald-300 ml-2">/年</span>
                      </div>
                      <div className="text-emerald-300 text-sm">
                        年間減価償却費
                      </div>
                    </div>

                    {/* Tax Savings Grid */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/10 rounded-2xl p-5">
                        <div className="text-emerald-300 text-sm mb-1">年間節税額</div>
                        <div className="text-2xl font-bold text-white">
                          {formatCurrency(taxResults.taxSavingsPerYear)}
                        </div>
                      </div>
                      <div className="bg-white/10 rounded-2xl p-5">
                        <div className="text-emerald-300 text-sm mb-1">節税率</div>
                        <div className="text-2xl font-bold text-white">
                          {taxResults.taxSavingsRate}%
                        </div>
                      </div>
                    </div>

                    {/* Total Tax Savings */}
                    <div className="bg-emerald-400/20 border border-emerald-400/30 rounded-2xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-emerald-300 text-sm">
                          {taxResults.depreciationYears}年間の総節税額
                        </span>
                        <i className="ri-shield-check-line text-emerald-400 text-xl"></i>
                      </div>
                      <div className="text-4xl font-bold text-white mb-2">
                        {formatCurrency(taxResults.totalTaxSavings)}
                      </div>
                      <div className="text-emerald-300 text-sm">
                        実質投資コスト: {formatCurrency(taxResults.effectiveCost)}
                      </div>
                    </div>

                    {/* Tax Comparison */}
                    <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                      <div className="text-emerald-300 text-sm mb-4">
                        年間税額の比較（{selectedTaxRate.name}）
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 text-sm">投資前の税額</span>
                          <span className="text-white font-semibold">
                            {formatCurrency(taxResults.taxBeforeInvestment)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 text-sm">投資後の税額</span>
                          <span className="text-white font-semibold">
                            {formatCurrency(taxResults.taxAfterInvestment)}
                          </span>
                        </div>
                        <div className="pt-3 border-t border-white/10 flex items-center justify-between">
                          <span className="text-emerald-400 text-sm font-medium">年間削減額</span>
                          <div className="flex items-center gap-2 text-emerald-400">
                            <i className="ri-arrow-down-line"></i>
                            <span className="font-bold text-lg">
                              {formatCurrency(taxResults.annualTaxReduction)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-white/10">
                    <p className="text-emerald-300/60 text-xs mb-4">
                      ※ 本シミュレーションは一般的な計算に基づく参考値です。実際の税務処理については税理士にご相談ください。
                    </p>
                    <a
                      href="/#contact"
                      className="w-full py-4 bg-emerald-400 text-emerald-900 font-semibold rounded-xl hover:bg-emerald-300 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                      税務専門家との相談を予約
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center py-16">
                  <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mb-6">
                    <i className="ri-percent-line text-gray-400 text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    税務条件を設定してください
                  </h3>
                  <p className="text-gray-500 text-sm max-w-xs">
                    左側のパネルで投資額・課税所得・税率を設定し、「税務メリットを計算」ボタンをクリックしてください
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        )}

        {/* Feature Cards */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16"
        >
          <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
              <i className="ri-shield-check-line text-amber-600 text-2xl"></i>
            </div>
            <h4 className="font-bold text-gray-900 mb-2">短期減価償却</h4>
            <p className="text-gray-600 text-sm">
              ビルボード資産は5〜7年での減価償却が可能。税務上のメリットを最大化できます。
            </p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
              <i className="ri-funds-line text-green-600 text-2xl"></i>
            </div>
            <h4 className="font-bold text-gray-900 mb-2">安定広告収益</h4>
            <p className="text-gray-600 text-sm">
              長期契約による安定した広告収入。98%の高稼働率で継続的なキャッシュフローを実現。
            </p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
              <i className="ri-building-2-line text-emerald-600 text-2xl"></i>
            </div>
            <h4 className="font-bold text-gray-900 mb-2">ビルボード資産価値</h4>
            <p className="text-gray-600 text-sm">
              ラスベガスの成長とともに資産価値も上昇。長期的な資産形成に最適な投資先です。
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
